import type {IApiEndpoint, IOperationResponse} from "@localization/siteMap/siteMapInterfaces.ts";
import React, {memo, useCallback, useEffect, useMemo, useState} from "react";
import {
    Autocomplete,
    Box,
    Grid,
    IconButton,
    LinearProgress,
    ListItem,
    Paper,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {ArrowLeft, ArrowRight, GppBad} from "@mui/icons-material";
import {useApi} from "@contexts/hooks/api/useApi.ts";
import {HighlightText} from "@shared/specialComponents/HighlightText.tsx";
import {IDataDto} from "@models/shared/IDataDto.ts";
import {fullWidth} from "@models/helpers.ts";

interface IOptions<TTYpe = any | undefined> {
    debounceTime?: number;
    clearOnSelect?: boolean;
    initialValue?: TTYpe;
    pageSize?: number;
}

interface IDisplay {
    label?: string;
    placeholder?: string;
    loadingText?: string;
    name?: string;
}

export interface DefaultPesquisaAutoProps<TType, TFilters> {
    /**parameters**/
    endpoint: (data: IDataDto<TFilters>) => IApiEndpoint<TType[]>;
    searchField: keyof TFilters
    itemToString: (item: TType) => string;
    options?: IOptions<TType>
    display?: IDisplay
    textFieldProps?: Omit<React.ComponentProps<typeof TextField>, 'label' | 'placeholder' | 'variant' | 'type'>;
    /**events**/
    onSearch?: (values?: TType[]) => Promise<IOperationResponse<TType>>
    onItemSelected?: (item?: TType) => void;
    required?: boolean;
    disabled?: boolean;
}

export interface DefaultConsumerPesquisaAutoProps<TType, TFilters> extends Partial<Omit<DefaultPesquisaAutoProps<TType, TFilters>, 'endpoint' | 'searchData'>> {

}

interface IAutocompleteOption<T = string | {} | undefined | any> {
    value: T;
    /**
     * Utilizar com o arrowcomponent
     */
    position?: 'Top' | 'Bottom'
    key: number
}

// const PageSelector = memo((props: {
//     pagination: IPagination,
//     setPagination?: React.Dispatch<SetStateAction<IPagination>>,
//     setPage?: (v: number) => void,
// }) => {
//     const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
//
//     const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
//         const currentFocus = document.activeElement as HTMLElement;
//         setAnchorEl(event.currentTarget);
//     };
//
//     const handleClose = () => {
//         setAnchorEl(null);
//     };
//
//     const open = Boolean(anchorEl);
//     const id = open ? 'page-popover' : undefined;
//
//     return (
//         <div>
//             <Typography aria-describedby={id} onClick={handleClick}>
//                 {props.pagination.page}
//             </Typography>
//             <Popover
//                 id={id}
//                 open={open}
//                 anchorEl={anchorEl}
//                 onClose={() => {
//                 }}
//                 anchorOrigin={{
//                     vertical: 'bottom',
//                     horizontal: 'left',
//                 }}
//             >
//                 <Typography sx={{p: 2}}>The content of the Popover.</Typography>
//             </Popover>
//         </div>
//     );
// }, (prev, next) => {
//     return prev.pagination.page == next.pagination.page
// })

const displayDefault = {
    label: 'Pesquisa rapida',
    placeholder: 'Pesquisa rápida',
    loadingText: 'Carregando...'
} as IDisplay
const optionsDefault = {debounceTime: 1250, clearOnSelect: false, pageSize: 50} as IOptions

export const DefaultPesquisaAuto = <T extends {}, TFilters>(props: DefaultPesquisaAutoProps<T, TFilters>) => {
    const {Get} = useApi()
    const {
        endpoint,
        itemToString,
        searchField,
        onItemSelected,
        textFieldProps,
    } = props

    const display = useMemo(() => {
        return {...displayDefault, ...props.display}
    }, [props.display]);
    const options = useMemo(() => {
        return {
            ...optionsDefault,
            ...props.options
        }
    }, [props.options])

    const [searchFilter, setSearchFilter] = useState<string>(options.initialValue != null ? itemToString(options.initialValue) : '');
    const [items, setItems] = useState<T[]>(options.initialValue != null && [options.initialValue] || []);
    const [selected, setSelected] = useState<T | undefined>(options.initialValue ?? undefined);
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
    const [lastSearch, setLastSearch] = useState<{
        value: string | null,
        page: number
    }>({value: (options.initialValue ? itemToString(options.initialValue) : null), page: 1});
    const [unauthorized, setUnauthorized] = useState(false);
    const [filters, setFilters] = useState<IDataDto<TFilters>>({
        pagination: {page: 1, pageSize: options.pageSize ?? 10, total: 0, hasNext: false},
        data: {[searchField]: ''}
    } as IDataDto<TFilters>);
    const [canRefresh, setCanRefresh] = useState(true);

    const {pagination} = filters

    const availableItemsStr = useMemo<IAutocompleteOption<T | string | undefined>[]>(() => {
        let val: IAutocompleteOption[] = items.map((item, index) => ({value: itemToString(item), key: 1 + index}))
        if (pagination.pages != null && pagination.pages > 1) {
            if (val.length > 3) {
                val = [{
                    value: 'Página anterior', position: 'Top',
                    key: 1
                }, ...val]
            }
            val = [...val, {
                value: 'Próxima página', position: 'Bottom',
                key: 2
            }]
        }
        return val
    }, [items, pagination]);

    const handleSearch = useCallback(async (filters: IDataDto<TFilters>) => {
        setFilters(prev => ({...prev, pagination: {...prev.pagination, loading: true}}))
        return await Get<T[]>(endpoint({
            ...filters,
            data: {...filters.data, [searchField]: searchFilter}
        })).then(response => {
            if (response.items != null) {
                setItems(response.items);
                setFilters(prev => ({
                    ...prev,
                    pagination: {...prev.pagination, ...response.pagination, pageSize: prev.pagination.pageSize}
                })); // Ensure loading is off if we skip fetch
                if (response.status == 'Unauthorized') {
                    setUnauthorized(true);
                }
            }
        }).finally(() => {
            setCanRefresh(true);
            setFilters(prev => ({...prev, pagination: {...prev.pagination, ignoreCache: false, loading: false}})); // Ensure loading is off if we skip fetch
        })
    }, [filters.data, searchFilter, filters.pagination])

    useEffect(() => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        const trimmedFilter = searchFilter.trim();

        // Set timeout to fetch data
        const newTimeoutId = setTimeout(() => {
            // Avoid searching if the selected item's string representation perfectly matches the filter
            // This prevents re-fetching when an item is selected from the dropdown
            // But we should still search if the filter is different from the selected item's string representation
            if (selected && itemToString(selected) === searchFilter) {
                setFilters(prev => ({...prev, pagination: {...prev.pagination, loading: false}})); // Ensure loading is off if we skip fetch
                return;
            }
            // Skip search if the filter is empty and there's a selected item
            // This prevents losing the reference when clicking on the search field again
            // But allow search to proceed if the filter is not empty, even if there's a selected item
            if (trimmedFilter === '' && selected) {
                setFilters(prev => ({...prev, pagination: {...prev.pagination, loading: false}})); // Ensure loading is off if we skip fetch
                return;
            }
            // Skip search if the filter is the same as the last search
            // This prevents duplicate searches
            // But we should still allow the search to proceed if the user is actively typing
            if (trimmedFilter === lastSearch.value && lastSearch.page === pagination.page) {
                setFilters(prev => ({...prev, pagination: {...prev.pagination, loading: false}})); // Ensure loading is off if we skip fetch
                return;
            }

            setLastSearch(prev => ({...prev, value: trimmedFilter, page: prev.page})); // Update last search filter
            setFilters(prev => ({
                ...prev,
                pagination: {...prev.pagination, loading: true},
                data: {...prev.data, [searchField]: trimmedFilter}
            }));
            handleSearch(filters).finally(() => {
            })
        }, options?.debounceTime ?? 550);

        setTimeoutId(newTimeoutId);

        return () => {
            if (newTimeoutId) {
                clearTimeout(newTimeoutId);
            }
        };
    }, [searchFilter, endpoint, options?.debounceTime, pagination.pageSize, selected]); // Added selected and itemToString

    useEffect(() => {
        setFilters(prev => ({...prev, pagination: {...prev.pagination, page: 0}}))
    }, [filters.data]);

    useEffect(() => {
        if (pagination.page <= 0) {
            setFilters(prev => ({...prev, pagination: {...prev.pagination, page: 1}}))
            return
        }
        handleSearch(filters).finally(() => {
        })
    }, [pagination.page, pagination.ignoreCache == true]);

    useEffect(() => {
        if (canRefresh) {
            setCanRefresh(false)
        }
    }, [canRefresh]);

    useEffect(() => {
        if (onItemSelected != null) {
            onItemSelected(selected);
            if (options?.clearOnSelect == true) {
                setSearchFilter('');
            }
        }
    }, [selected, options?.clearOnSelect]);

    // Handlers
    const handleInputChange = (_event: React.SyntheticEvent, newValue: string) => {
        setSearchFilter(newValue);
        // If the user is typing and the new value doesn't match the selected item's string representation,
        // clear the selected state to allow the search to proceed
        if (selected && newValue !== itemToString(selected)) {
            setSelected(undefined);
        }
    }

    const handleChange = useCallback((_event: React.SyntheticEvent, newValue: string | undefined | IAutocompleteOption<T>) => {
        // If newValue is a string, it's likely coming from freeSolo input or clearing
        // If newValue is an object (T), it's from selecting an option
        if (typeof newValue === 'string') {
            // Explicitly set selected to null if the input doesn't match any known item string
            if (!items.some(item => itemToString(item) === newValue)) {
                setSelected(undefined);
            }
        } else if (newValue != null && newValue as IAutocompleteOption<T>) { // newValue is of type T
            if (newValue.position != null) {
                return;
            }
            const newval = items.find(x => itemToString(x) == (newValue.value as unknown as string))

            setSearchFilter(newValue.value as unknown as string);
            setSelected(newval); // Update selected state
		} else { // newValue is null (cleared)
            setSearchFilter('');
            setSelected(undefined);
        }
    }, [itemToString, items]);

    const PageArrow = memo((props: React.HTMLAttributes<HTMLLIElement> & {
        key: any,
        label: string,
        onClick: () => void,
        arrowPosition: 'Left' | 'Right',
        disabled?: boolean,
    }) => {
        const [hover, setHover] = useState(false)

        const {label: _label, onClick: _onClick, arrowPosition: _arrowPosition, ...rest} = props;

        const replaceEvents = {
            onMouseMove: () => {
            },
            onMouseEnter: (_e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
                setHover(true)
            },
            onMouseLeave: (_e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
                setHover(false)
            },
            onTouchStart: (_e: React.TouchEvent<HTMLLIElement>) => {
                setHover(true)
            },
            onTouchEnd: (_e: React.TouchEvent<HTMLLIElement>) => {
                setHover(false)
            },
        }

        return <ListItem  {...rest}
                          {...replaceEvents}
                          aria-disabled={props.disabled}
                          sx={{backgroundColor: hover ? 'rgba(0, 0, 0, 0.35)' : undefined, cursor: 'pointer'}}
                          onClick={props.onClick}>
            <Grid size={12}
                  justifyContent={'space-between'}
                  sx={{...fullWidth}}
                  container>
                {props.arrowPosition == "Left" && <Grid size={1}>
                    <ArrowLeft/>
                </Grid>}
                <Grid size={11} container justifyContent={"center"}>
                    <Typography color={props.disabled ? 'textDisabled' : hover ? 'info' : 'primary'}
                    >{props.label}</Typography>
                </Grid>
                {props.arrowPosition == "Right" && <Grid size={1}>
                    <ArrowRight/>
                </Grid>}
            </Grid>
        </ListItem>
    })


    return (
        <Paper elevation={3}>
            <Autocomplete
                options={availableItemsStr}
                loading={pagination.loading}
                inputValue={searchFilter}
                defaultValue={{value: options?.initialValue ? itemToString(options.initialValue) : '', key: 0}}
                loadingText={display?.loadingText}
                disabled={unauthorized || (textFieldProps?.disabled ?? false)}
                onInputChange={(e, v) => handleInputChange(e, v)}
                onFocus={() => {
                    if (!selected || selected == options?.initialValue) {
                        setSearchFilter(lastSearch.value ?? '');
                    }
                }}
                onChange={(e, v) => handleChange(e, v as (string | IAutocompleteOption<T> | undefined))}
                freeSolo
                filterOptions={(x) => x} // Disable built-in filtering as results come from API
                renderInput={(params) => (
                    <Grid size={12} container>
                        <Grid size={12}>
                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                {unauthorized &&
                                    <Tooltip title={'Sem permissão para utilizar'}><IconButton color={'error'}>
                                        <GppBad/>
                                    </IconButton>
                                    </Tooltip>}
                                <TextField
                                    {...params}
                                    {...textFieldProps}
                                    sx={{minWidth: '150px'}}
                                    disabled={unauthorized || (props.disabled)}
                                    required={props.required}
                                    label={unauthorized ? 'Você não possui permissão para utilizar essa consulta!' : `${display?.label ?? ""} ${display?.name != null ? `(${display.name})` : ""}`}
                                    error={unauthorized || (props.required && selected == null) || (textFieldProps?.required && (options?.clearOnSelect != true && selected == undefined))}
                                    placeholder={`${display?.placeholder ?? ""} ${display?.name != null ? `(${display.name})` : ""}`}
                                    variant="filled"
                                    type="text"
                                />
                            </Box>
                            <Grid size={12}>
                                <LinearProgress value={pagination.loading ? 100 : 0}
                                                variant={pagination.loading ? 'indeterminate' : 'determinate'}/>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
                getOptionLabel={(option) => {
                    if (option == '') {
                        return ''
                    }
                    if (option != null && typeof option == "object" && option as IAutocompleteOption) {
                        return option.value as string
                    }
                    return ''
                }}
                renderOption={(props, option) => {
                    if (option.position != null) {
                        const positionSx = option.position == 'Top' ? {mb: 1} : {mt: 1}
                        return (<Grid key={option.key} size={12} container justifyContent={'space-between'}
                                      alignItems={'center'}
                                      sx={{...positionSx}}>
                            <Grid size={5.5}>
                                <PageArrow {...props}
                                           arrowPosition={'Left'}
                                           key={'arrowLeftUp'}
                                           disabled={pagination.page <= 1 || pagination.loading}
                                           onClick={() => {
                                               setFilters(prev => ({
                                                   ...prev,
                                                   pagination: {
                                                       ...prev.pagination,
                                                       page: prev.pagination.page - 1,
                                                       loading: true
                                                   }
                                               }))
                                           }} label="Anterior"/>
                            </Grid>
                            <Grid size={1}>
                                <Typography>{pagination.page}</Typography>
                                {/*<PageSelector pagination={pagination}
                                              setPage={(v) => {
                                                  setFilters(prev => ({
                                                      ...prev,
                                                      pagination: {
                                                          ...prev.pagination,
                                                          page: v,
                                                          loading: true
                                                      }
                                                  }))
                                              }}/>*/}
                            </Grid>
                            <Grid size={5.5}>
                                <PageArrow {...props}
                                           arrowPosition={'Right'}
                                           key={'arrowLeftDown'}
                                           disabled={!pagination.hasNext || pagination.loading}
                                           onClick={() => {
                                               setFilters(prev => ({
                                                   ...prev,
                                                   pagination: {
                                                       ...prev.pagination,
                                                       page: prev.pagination.page + 1,
                                                       loading: true
                                                   }
                                               }))
                                           }} label="Proxima"/>
                            </Grid>
                        </Grid>)
                    }

                    const optionStr = option.value as string;
                    const Child = memo((_: { canRefresh: boolean }) => HighlightText({
                        input: optionStr, search: searchFilter, options: {
                            minLenght: 2
                        }
                    }), (_, next) => !next.canRefresh);

                    return (
                        <ListItem {...props} key={optionStr}>
                            <Typography> <Child canRefresh={canRefresh}/></Typography>
                        </ListItem>
                    );
                }}
                noOptionsText="Nenhum resultado encontrado"
            />
        </Paper>
    )
}
